import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAdmin } from '@/contexts/AdminContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import logger from '@/utils/logger';
import {
  Package,
  MapPin,
  MessageSquare,
  Mail,
  TrendingUp,
  Eye,
  ArrowRight,
  HardDrive,
  RefreshCw,
  AlertTriangle,
  ImageOff,
  Search
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { getAuthHeaders } = useAdmin();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/reports/dashboard`, {
        headers: getAuthHeaders()
      });
      setDashboard(response.data);
    } catch (error) {
      logger.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Enquiries',
      value: dashboard?.stats?.total_enquiries || 0,
      icon: MessageSquare,
      color: 'bg-blue-500',
      path: '/admin/reports/enquiries'
    },
    {
      title: 'Contact Messages',
      value: dashboard?.stats?.total_contacts || 0,
      icon: Mail,
      color: 'bg-green-500',
      path: '/admin/reports/contacts'
    },
    {
      title: 'Tour Packages',
      value: dashboard?.stats?.total_packages || 0,
      icon: Package,
      color: 'bg-purple-500',
      path: '/admin/packages'
    },
    {
      title: 'Destinations',
      value: dashboard?.stats?.total_destinations || 0,
      icon: MapPin,
      color: 'bg-orange-500',
      path: '/admin/destinations'
    }
  ];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Dashboard</h1>
        <p className="text-slate-500">Welcome to Dnotours Admin Panel</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Card
            key={`stat-${stat.title}`}
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => navigate(stat.path)}
            data-testid={`stat-card-${stat.title.toLowerCase().replace(' ', '-')}`}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{stat.title}</p>
                  <p className="text-3xl font-bold text-slate-800 mt-1">{stat.value}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-full`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Enquiries */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-semibold">Recent Enquiries</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/reports/enquiries')}
              className="text-red-600 hover:text-red-700"
            >
              View All <ArrowRight className="ml-1 w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {dashboard?.recent_enquiries?.length > 0 ? (
              <div className="space-y-3">
                {dashboard.recent_enquiries.map((enquiry) => (
                  <div
                    key={enquiry.id || `enq-${enquiry.name}-${enquiry.email}`}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-slate-800">{enquiry.name}</p>
                      <p className="text-sm text-slate-500">{enquiry.destination || 'General Enquiry'}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      enquiry.status === 'new' ? 'bg-blue-100 text-blue-700' :
                      enquiry.status === 'contacted' ? 'bg-yellow-100 text-yellow-700' :
                      enquiry.status === 'converted' ? 'bg-green-100 text-green-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {enquiry.status || 'new'}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-8">No enquiries yet</p>
            )}
          </CardContent>
        </Card>

        {/* Top Packages */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-semibold">Top Packages</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/reports/packages')}
              className="text-red-600 hover:text-red-700"
            >
              View Report <ArrowRight className="ml-1 w-4 h-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {dashboard?.top_packages?.length > 0 ? (
              <div className="space-y-3">
                {dashboard.top_packages.map((pkg) => (
                  <div
                    key={pkg.package_code || pkg.title}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono bg-slate-200 px-2 py-1 rounded">
                        #{pkg.package_code}
                      </span>
                      <div>
                        <p className="font-medium text-slate-800">{pkg.title}</p>
                        <p className="text-sm text-slate-500">{pkg.destination}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Eye size={14} /> {pkg.views || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <TrendingUp size={14} /> {pkg.enquiries || 0}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-8">No packages yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Contacts */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg font-semibold">Recent Contact Messages</CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/reports/contacts')}
            className="text-red-600 hover:text-red-700"
          >
            View All <ArrowRight className="ml-1 w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {dashboard?.recent_contacts?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-slate-500 border-b">
                    <th className="pb-3 font-medium">Name</th>
                    <th className="pb-3 font-medium">Email</th>
                    <th className="pb-3 font-medium">Subject</th>
                    <th className="pb-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard.recent_contacts.map((contact) => (
                    <tr key={contact.id || `contact-${contact.email}`} className="border-b last:border-0">
                      <td className="py-3 font-medium text-slate-800">{contact.name}</td>
                      <td className="py-3 text-slate-600">{contact.email}</td>
                      <td className="py-3 text-slate-600">{contact.subject}</td>
                      <td className="py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          contact.status === 'new' ? 'bg-blue-100 text-blue-700' :
                          contact.status === 'responded' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {contact.status || 'new'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-slate-500 py-8">No contact messages yet</p>
          )}
        </CardContent>
      </Card>
      {/* Image Storage Tools */}
      <Card className="border-amber-200 bg-amber-50/50" data-testid="image-tools-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            Image Storage Tools
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Broken Image Scanner */}
          <div className="p-4 bg-white rounded-lg border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <ImageOff className="h-4 w-4 text-red-500" />
              <h3 className="font-semibold text-sm text-slate-800">Scan for Broken Images</h3>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Find packages and tours with missing images that need to be re-uploaded.
            </p>
            <Button
              onClick={async () => {
                setScanning(true);
                setScanResult(null);
                try {
                  const resp = await axios.get(`${API_URL}/api/admin/scan-broken-images`, { headers: getAuthHeaders() });
                  setScanResult(resp.data);
                } catch (err) {
                  setScanResult({ error: err.response?.data?.detail || 'Scan failed' });
                } finally {
                  setScanning(false);
                }
              }}
              disabled={scanning}
              size="sm"
              className="bg-red-600 hover:bg-red-700"
              data-testid="scan-broken-images-btn"
            >
              {scanning ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Scanning...</>
              ) : (
                <><Search className="h-4 w-4 mr-2" /> Scan Now</>
              )}
            </Button>
            {scanResult && !scanResult.error && (
              <div className="mt-3" data-testid="scan-result">
                {scanResult.broken_count === 0 ? (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                    <p className="font-medium text-green-800">All images are healthy!</p>
                    <p className="text-green-600">{scanResult.ok_count} image(s) verified.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
                      <p className="font-medium text-red-800">{scanResult.broken_count} broken image(s) found</p>
                      <p className="text-red-600 text-xs mt-1">
                        These images were lost from local storage. Please edit each item and re-upload the image.
                      </p>
                    </div>
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {scanResult.broken_images.map((item) => (
                        <div key={`${item.collection}-${item.id}-${item.field}`} className="flex items-center justify-between p-2 bg-slate-50 rounded text-xs border">
                          <div>
                            <span className="font-medium text-slate-800">{item.name}</span>
                            <span className="text-slate-400 ml-2">({item.collection} #{item.id})</span>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-xs px-2"
                            onClick={() => {
                              const routes = {
                                packages: '/admin/packages',
                                theme_tours: '/admin/theme-tours',
                                destinations: '/admin/destinations',
                                blog_posts: '/admin/blog',
                              };
                              navigate(routes[item.collection] || '/admin/packages');
                            }}
                          >
                            Edit
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {scanResult?.error && (
              <p className="mt-3 text-sm text-red-600">{scanResult.error}</p>
            )}
          </div>

          {/* Migration */}
          <div className="p-4 bg-white rounded-lg border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <HardDrive className="h-4 w-4 text-amber-600" />
              <h3 className="font-semibold text-sm text-slate-800">Migrate Local Images to Cloud</h3>
            </div>
            <p className="text-xs text-slate-500 mb-3">
              Upload any images still on local disk to persistent cloud storage so they survive restarts.
            </p>
            <Button
              onClick={async () => {
                setMigrating(true);
                setMigrationResult(null);
                try {
                  const resp = await axios.post(`${API_URL}/api/admin/migrate-images`, {}, { headers: getAuthHeaders() });
                  setMigrationResult(resp.data);
                } catch (err) {
                  setMigrationResult({ error: err.response?.data?.detail || 'Migration failed' });
                } finally {
                  setMigrating(false);
                }
              }}
              disabled={migrating}
              size="sm"
              variant="outline"
              className="border-amber-300 hover:bg-amber-100"
              data-testid="migrate-images-btn"
            >
              {migrating ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Migrating...</>
              ) : (
                <><HardDrive className="h-4 w-4 mr-2" /> Run Migration</>
              )}
            </Button>
            {migrationResult && !migrationResult.error && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                <p className="font-medium text-green-800">Migration Complete</p>
                <p className="text-green-700">{migrationResult.migrated_files} file(s) migrated, {migrationResult.db_urls_normalized} DB URL(s) normalized</p>
                {migrationResult.details?.failed?.length > 0 && (
                  <p className="text-red-600 mt-1">{migrationResult.details.failed.length} file(s) failed</p>
                )}
              </div>
            )}
            {migrationResult?.error && (
              <p className="mt-3 text-sm text-red-600">{migrationResult.error}</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
